// A person on the Team tab (`modal=person&person=<id>`, finance-real, the user 2026-10-23: "clicking here opens a modal
// with all the invoices sent to the person, if I press on the invoice then it opens the invoice modal"). Named after them,
// with the row's Owner and External badges. On top what they are owed now, and paid ahead when they are, the Team row's
// figures (TD-102). Then their team invoices, every status, the newest made first, each the shared InvoiceListRow (the
// client drawer's too): its name, its status-date line and its claims, its status (the shared pill, read-only: it changes
// in the invoice's drawer, DD-062) and its amount. A row opens the invoice's drawer
// in this one's place, replacing it in the history (DD-045's hand-over); saved rows carry no buttons. With owed claims on
// no team invoice yet, New team invoice sits in the header: it stacks one level over this drawer (`stack=new-team-invoice`,
// DD-045's create flow), for this person, their claims ticked, and closing it comes back here; making the draft (or
// opening the one they have) hands over to that invoice's drawer. New job (the user 2026-10-24: "add line items against a
// freelancer when they do extra work for the agency") hands over to their draft team invoice with the job form stacked
// over it (`stack=line-job-form&client=agency`), the team invoice's own New job starting on Agency (no client); with no draft
// it makes an empty one first, the way New team invoice does, so their owed claims stay theirs to add.
import { Badge } from "@/components/ui/Badge";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG, EntityCreateButton } from "@/core/entityConfig";
import { TEAM_INVOICE_STATES } from "../clientPage/lineJobText";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { InvoiceListRow } from "../components/InvoiceListRow";
import { claimsCount, invoiceDateLine } from "../utils/names";
import { EXTERNAL } from "../utils/people";
import { usePersonDrawer } from "./hooks/usePersonDrawer";
import { NewTeamInvoiceDrawer } from "./NewTeamInvoiceDrawer";

const TEAM = ENTITY_CONFIG.teamInvoice;
// `contract` names the user's job now (the entityConfig shadow).
const JOB = ENTITY_CONFIG.contract;
const heading = "text-[11px] uppercase tracking-wide text-gray-500 mb-1";

interface Props {
  personId: string;
  /** New team invoice stacked over it (`stack=new-team-invoice`). */
  creating: boolean;
  onCreate: (open: boolean) => void;
  /** A team invoice, in this drawer's place. */
  onOpenInvoice: (invoiceId: string) => void;
  /** Their draft team invoice in this drawer's place, the job form stacked over it. */
  onNewJob: (invoiceId: string) => void;
  onClose: () => void;
}

export function PersonDrawer({ personId, creating, onCreate, onOpenInvoice, onNewJob, onClose }: Props) {
  const { teamQuery, person, invoicesQuery, invoices, people, hasOpenClaims, draftId, makeDraft } = usePersonDrawer(personId);
  if (!person) {
    return (
      <FinanceDrawer title={ENTITY_CONFIG.person.label} onClose={onClose}>
        <p className="text-sm text-gray-500">{teamQuery.isError ? teamQuery.error.message : teamQuery.data ? "This person is not on the team." : "Loading…"}</p>
      </FinanceDrawer>
    );
  }
  const firstName = person.name.split(" ")[0];
  const newJob = () => {
    if (draftId) return onNewJob(draftId);
    if (!makeDraft.isPending) makeDraft.mutateAsync({ personId: person.id, claimIds: [] }).then((i) => onNewJob(i.id), () => undefined);
  };
  const aside = (
    <span className="flex flex-wrap items-center gap-2">
      {person.isOwner && <Badge tone="indigo">Owner</Badge>}
      {person.external && <Badge tone="gray">{EXTERNAL}</Badge>}
      {/* DD-056: a header create button is an EntityCreateButton. */}
      {hasOpenClaims && <EntityCreateButton entity="teamInvoice" variant="primary" size="sm" onClick={() => onCreate(true)} />}
    </span>
  );
  return (
    <>
      <FinanceDrawer title={person.name} titleAside={aside} onClose={onClose}>
        <div className="space-y-5 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-500">Owed now</div>
              <MoneyText amountMinor={person.owedMinor} strong={person.owedMinor > 0} balance />
            </div>
            {person.aheadMinor > 0 && (
              <div>
                <div className="text-xs text-gray-500">Paid ahead</div>
                <MoneyText amountMinor={person.aheadMinor} />
              </div>
            )}
          </div>

          <section className="space-y-2">
            <h3 className={heading}>{TEAM.plural}</h3>
            {!invoicesQuery.data ? (
              <p className="text-gray-500">{invoicesQuery.isError ? invoicesQuery.error.message : `Reading ${firstName}'s ${TEAM.plural.toLowerCase()}…`}</p>
            ) : invoices.length === 0 ? (
              <p className="text-gray-400">{`No invoices from ${firstName} yet.`}</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {invoices.map((i) => (
                  <li key={i.id}>
                    <InvoiceListRow
                      name={i.title}
                      dateLine={invoiceDateLine({ kind: "team", ...i })}
                      count={claimsCount(i.claims.length)}
                      look={TEAM_INVOICE_STATES[i.status]}
                      amountMinor={i.totalMinor}
                      onOpen={() => onOpenInvoice(i.id)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Under the list, as a draft team invoice's More: the header keeps New team invoice alone, so it fits 390px. */}
          <section className="space-y-2">
            <h3 className={heading}>More</h3>
            <EntityCreateButton entity="contract" variant="secondary" onClick={newJob} />
            <p className="text-xs text-gray-500">{`Extra work ${firstName} does: a ${JOB.label.toLowerCase()} with their claim on it, on their draft ${TEAM.label.toLowerCase()}.`}</p>
          </section>
        </div>
      </FinanceDrawer>
      {creating && invoicesQuery.data && (
        <NewTeamInvoiceDrawer people={people} personId={person.id} stacked onOpened={onOpenInvoice} onClose={() => onCreate(false)} />
      )}
    </>
  );
}
